import { useEffect, useState } from 'react';
import {
  Modal,
  Input,
  List,
  Empty,
  Spin,
  Avatar,
  Space,
  Tag,
  message,
  Divider,
} from 'antd';
import { BellOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../lib/api-client';

interface PastNotification {
  id: number;
  type: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
  // Populated by the backend for admin-triggered notifications
  // (VERIFICATION_REJECTED / VERIFICATION_APPROVED / BLOCKED / UNBLOCKED).
  // Correlated by time-window against the audit_logs table — null when no
  // matching admin action is found within ±5 min of the notification.
  actionBy?: {
    id: number;
    username: string;
    fullName: string | null;
  } | null;
}

interface PushNotificationModalProps {
  open: boolean;
  userId: number | null;
  userName?: string;
  userPhone?: string;
  /** When false, render an inline warning that FCM delivery is unlikely. */
  hasFcmToken?: boolean;
  onClose: () => void;
  onSent?: () => void;
}

/**
 * Send a push to a single user, plus show their recent notification history
 * inline so operators can see what's already been pushed before duplicating
 * the same message.
 */
export const PushNotificationModal: React.FC<PushNotificationModalProps> = ({
  open,
  userId,
  userName,
  userPhone,
  hasFcmToken,
  onClose,
  onSent,
}) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState<PastNotification[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = async () => {
    if (!userId) return;
    setHistoryLoading(true);
    try {
      const res = await apiClient.get(`/admin/users/${userId}/notifications`);
      setHistory(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch (err: any) {
      // Don't surface errors here — history is auxiliary; the form still
      // works. Just leave the list empty.
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (open && userId) {
      setTitle('');
      setBody('');
      fetchHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const send = async () => {
    if (!userId) return;
    if (!title.trim() || !body.trim()) {
      message.warning('Введите заголовок и текст уведомления');
      return;
    }
    setSending(true);
    try {
      const res = await apiClient.post('/admin/notifications/send', {
        title: title.trim(),
        body: body.trim(),
        targetType: 'USER',
        targetId: userId,
      });
      const delivered = res.data?.delivered ?? 0;
      const failed = res.data?.failed ?? 0;
      if (delivered > 0) {
        message.success('✅ Уведомление доставлено');
      } else if (failed > 0) {
        message.error('FCM не смог доставить (нет push-токена?)');
      } else {
        message.info('Отправлено, но FCM не подтвердил доставку');
      }
      setTitle('');
      setBody('');
      // Refresh history so the just-sent message shows up immediately.
      await fetchHistory();
      onSent?.();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Не удалось отправить уведомление',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <BellOutlined />
          <span>
            Push-уведомление
            {userName ? ` — ${userName}` : ''}
            {userPhone ? ` (${userPhone})` : ''}
          </span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      {hasFcmToken === false && (
        <div
          style={{
            background: '#FFF7E6',
            border: '1px solid #FFD591',
            padding: 8,
            borderRadius: 6,
            marginBottom: 12,
            fontSize: 13,
            color: '#874D00',
          }}
        >
          ⚠️ У пользователя нет FCM-токена — push, скорее всего, не дойдёт.
          Уведомление всё равно сохранится в БД.
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
          Заголовок
        </label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Yangi xabar"
          maxLength={100}
          showCount
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
          Текст
        </label>
        <Input.TextArea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Введите текст уведомления..."
          rows={4}
          maxLength={300}
          showCount
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '6px 14px',
            border: '1px solid #D9D9D9',
            background: '#fff',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={send}
          disabled={sending || !title.trim() || !body.trim()}
          style={{
            padding: '6px 14px',
            border: 'none',
            background:
              sending || !title.trim() || !body.trim() ? '#A7F3D0' : '#10B981',
            color: '#fff',
            borderRadius: 6,
            cursor:
              sending || !title.trim() || !body.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <SendOutlined /> {sending ? 'Отправка…' : 'Отправить'}
        </button>
      </div>

      <Divider style={{ margin: '20px 0 12px' }}>История уведомлений</Divider>

      {historyLoading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      ) : history.length === 0 ? (
        <Empty description="Уведомлений ещё не было" />
      ) : (
        <List
          dataSource={history}
          itemLayout="horizontal"
          renderItem={(n) => (
            <List.Item key={n.id} style={{ alignItems: 'flex-start' }}>
              <List.Item.Meta
                avatar={
                  <Avatar
                    icon={<BellOutlined />}
                    style={{
                      backgroundColor: n.isRead ? '#BDC3C7' : '#3B82F6',
                    }}
                  />
                }
                title={
                  <Space size="small" wrap>
                    <strong>{n.title}</strong>
                    <span style={{ color: '#95A5A6', fontSize: 12 }}>
                      {dayjs(n.createdAt).format('D MMMM, YYYY | HH:mm')}
                    </span>
                    {n.type && (
                      <Tag color="default" style={{ fontSize: 11 }}>
                        {n.type}
                      </Tag>
                    )}
                    {/* Admin who triggered this notification — set on
                        verify/reject/block/unblock pushes via the
                        audit-log correlation in listUserNotifications. */}
                    {n.actionBy && (
                      <Tag color="blue" style={{ fontSize: 11 }}>
                        by{' '}
                        {n.actionBy.fullName?.trim() ||
                          n.actionBy.username ||
                          `Staff #${n.actionBy.id}`}
                      </Tag>
                    )}
                    {n.isRead && (
                      <Tag color="default" style={{ fontSize: 11 }}>
                        прочитано
                      </Tag>
                    )}
                  </Space>
                }
                description={
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      color: '#2C3E50',
                      lineHeight: 1.5,
                      fontSize: 13,
                    }}
                  >
                    {n.body}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};
